/* Imported from the module rather than the `brand-config` barrel, which pulls in all 27 brand
   files; the services layer only needs the codes. */
import BRAND_CODES from 'brand-config/brand-codes';

export const SERVICE_AUTHORIZATION_ERR_MSG =
  'You must agree to the service authorization before creating your account.';

/**
 * The five-step flow asks for the same consent one step later than flex does, so Figma words
 * its validation around completing rather than creating the account (frame 156:19334).
 */
export const COMPLETE_ACCOUNT_AUTHORIZATION_ERR_MSG =
  'You must agree to the service authorization before completing your account.';

export const FLEX_AUTHORIZATIONS = {
  billed: [
    'BILLING',
    'REFUND',
    'terms',
    'privacy',
    'servicing',
    'general authorization',
  ],
  complimentary: ['terms', 'privacy', 'servicing', 'general authorization'],
};

/**
 * The AOL / partner flow sends its consent in one shot like flex's complimentary path, and never
 * the billing pair — the offer is already paid for by the partner
 * (aol-single-page-enrollment: enrollment.service.ts:1128). The member's two consent checkboxes
 * gate the submit rather than travelling with it, so this always ships.
 */
export const AOL_AUTHORIZATIONS = [
  'terms',
  'privacy',
  'servicing',
  'general authorization',
];

/**
 * The five-step flow authorizes incrementally, one array per PATCH, rather than sending the
 * whole set up front like flex does (enrollment.service.ts:189, :780, :662).
 */
export const FIVE_STEP_AUTHORIZATIONS = {
  account: ['terms', 'privacy', 'servicing'],
  personalInfo: ['general authorization'],
  billing: ['billing', 'refund'],
};

/**
 * `?enrollmentStep=` values the five-step endpoint accepts. The account step is a POST to the
 * bare collection URL and so has no step value.
 */
export const FIVE_STEP_API_STEPS = {
  name: 'name',
  address: 'address',
  contactInfo: 'contactInfo',
  personalInfo: 'personalInfo',
  billingInfo: 'billingInfo',
};

/**
 * identityWeb holds each step for two seconds behind an "Updating Account" status before it
 * persists (e.g. enrollment-name-details.component.ts). Preserved so the flow paces the same
 * as production; the button stays in its `getPending` loading state across the window.
 */
export const ENROLLMENT_STEP_DELAY_MS = 2000;

/**
 * `offer.billingType` values. identityWeb branches the plan-summary copy across all of these
 * (enrollment-plan-summary.component.html:15-47).
 */
export const BILLING_TYPES = {
  complimentary: 'complimentary',
  retailCcBilled: 'retail cc billed',
  companyBilled: 'company billed',
  ebCcBilled: 'eb cc billed',
  ebEmployerPaid: 'eb employer paid',
  ebPayrollDeduct: 'eb payroll deduct',
  ebBuyUpPayrollDeduct: 'eb buy up payroll deduct',
  ebBuyUpCcBilled: 'eb buy up cc billed',
};

/**
 * Brands whose redeemed offers enrol through the thin flow. PrivacyShield-only — identityWeb
 * has no thin enrollment, so there is no Angular branch to mirror; every brand outside this
 * list keeps identityWeb's flex-or-five-step decision.
 */
export const THIN_ENROLLMENT_BRAND_CODES: string[] = [
  BRAND_CODES.privacyShield,
  BRAND_CODES.privacyMedicalShield,
];

/** Marks a brand whose every plan enrols through flex, whatever the offer's plan code. */
export const FLEX_ALL_PLANS = 'all' as const;

/**
 * Brands whose redeemed offers enrol through the flex flow rather than the five-step flow, and
 * which of their plans do. `FLEX_ALL_PLANS` covers every plan the brand sells; a plan-code array
 * limits flex to those plans and leaves the rest on five-step.
 *
 * Minor Defense enrols entirely through flex; MedicalShield only on the plans listed
 * (activate-details.component.ts:223-240). Bringing another brand onto flex enrollment is an
 * entry here and nothing else — `isFlexEnrollmentOffer` in `./utils` is the only reader.
 */
export const FLEX_ENROLLMENT_BRAND_PLANS: Record<
  string,
  string[] | typeof FLEX_ALL_PLANS
> = {
  [BRAND_CODES.minorDefense]: FLEX_ALL_PLANS,
  [BRAND_CODES.medicalShield]: ['medshldind', 'medshldfam', 'medsld2fam'],
};

/**
 * Where the redeem page sends a thin offer. The flow reads its offer and activation code from
 * the store rather than the URL, so this path takes no query parameters.
 */
export const THIN_ENROLLMENT_PATH = '/enrollment/thin';

/** Where the redeem page sends a flex offer. */
export const FLEX_ENROLLMENT_PATH = '/enrollment/flex';

/**
 * Where the redeem page sends codes that are neither thin nor flex offers — the classic
 * multi-step flow (activate-details.component.ts). It no longer sits on the enrollment index,
 * which is now a redirect onto the redeem page.
 */
export const FIVE_STEP_ENROLLMENT_PATH = '/enrollment/general-info';

/**
 * Where `/enrollment/activate/:client` sends a client whose settlement is not final yet. The flow
 * reads its company from `?companyCode=`, which callers append.
 */
export const LIGHT_ENROLLMENT_PATH = '/enrollment/light';

/** Where every enrollment flow sends a member who has nothing to enrol into yet. */
export const ACTIVATE_CODE_PATH = '/enrollment/activate';

/** The terminal screen every enrollment flow ends on, whichever flow the member came through. */
export const CREATING_DASHBOARD_PATH = '/enrollment/creating-dashboard';

/** Home Title Guardian's AOL single-page enrollment (enrollment.routing.ts:86-90). */
export const AOL_ENROLLMENT_PATH = '/enrollment/aol';

/** The same screen for Financial Shield's partner offers (enrollment.routing.ts:91-95). */
export const PARTNER_ENROLLMENT_PATH = '/enrollment/partner';

/**
 * Where AOL sends a Home Title Guardian member before enrollment: the token is checked here and
 * the offer it resolves to is handed to `AOL_ENROLLMENT_PATH`
 * (app-routing.module.ts:404-407, verify-token.component.ts:36-50).
 */
export const AOL_PRE_ENROLL_PATH = '/aol/PreEnroll/AOLRequest';

/**
 * The SKU each brand's partner sends with the token, and the only value that brand's screen
 * accepts (aol-single-page-enrollment.component.ts:63, verify-token.component.ts:36).
 */
export const AOL_SKUS: Record<string, string> = {
  [BRAND_CODES.homeTitleGuardian]: 'htitleind',
  [BRAND_CODES.financialShield]: 'finsecind',
};

/**
 * Brands that enrol through the AOL / partner single-page screen. Each reaches it on its own
 * route, so the guard on that route is what admits the brand; this list is what the guards read.
 */
export const AOL_BRAND_CODES: string[] = [
  BRAND_CODES.homeTitleGuardian,
  BRAND_CODES.financialShield,
];

/** Page-level failure copy for the AOL submit, worded as flex's is. */
export const AOL_SUBMIT_ERR_MSG =
  'We are unable to create your account. Please try again or contact the Customer Care Center.';

/**
 * Clients whose breach programme no longer enrols here. identityWeb hands them straight to the
 * marketing site instead of showing a form they cannot use
 * (activate-details.component.ts:128, :149). Angular's list repeats `duraserv`; deduplicated.
 */
export const BREACH_REDIRECT_CLIENTS = [
  'wsu',
  'experian',
  'duraserv',
  'erb',
  'pp',
  'spatech',
  'nbeo',
  'nvppbc',
];

export const BREACH_REDIRECT_URL = 'https://identityguard.com';

/**
 * Breach-programme short links that hand the member to the redeem page
 * (app-routing.module.ts:314-395). `itrz` and `nwl` redeem under a different client code than
 * the path they arrive on, so these cannot be served by the `:client` catch-all.
 */
export const BREACH_INFORMATION_REDIRECTS = [
  { path: 'kemp', client: 'kemp' },
  { path: 'itrz', client: 'itcz' },
  { path: 'gecb', client: 'gecb' },
  { path: 'nwl', client: 'ntwl' },
];

/**
 * Clients that issue a printed instruction sheet alongside the activation code, offered as a
 * second button on the redeem screen (activate-details.component.ts:270-278).
 */
export const ENROLLMENT_INSTRUCTION_PDFS: Record<string, string> = {
  aflac: '/enrollment-documents/mshield_complete_AFLAC.pdf',
  aflacmd: '/enrollment-documents/md_aflacmd_enrollment_instruction.pdf',
};

/**
 * The route namespace every terminal enrollment failure lives under. Arriving here is a decision
 * the flow made for the member, not a navigation they chose, so the unsaved-changes guard steps
 * aside for it (identityWeb bypasses its own guard the same way, on a `/bypass-unsaved/` URL).
 */
export const ERROR_PAGE_PATH_PREFIX = '/error-page';

/* Enrollment failure destinations. routes.tsx has no route-constant file. */
export const ENROLLMENT_ERROR_PAGES = {
  brandNotMatched: '/error-page/brand-not-matched',
  brandNotValid: '/error-page/brand-not-valid',
  idauthStatusDeny: '/error-page/idauth-status-deny',
  offerNotFound: '/error-page/offer-not-found',
  somethingWentWrong: '/error-page/something-went-wrong',
  /* identityWeb shows these as a blocking dialog (PidFailureComponent) rather than a page;
     privacyShield has no enrollment-scoped modal pattern, so each is its own route. */
  identityNotVerified: '/error-page/identity-not-verified',
  invalidParticipantToken: '/error-page/invalid-participant-token',
  serviceEndRequest: '/error-page/service-end-request',
  invitationWithdrawn: '/error-page/invitation-withdrawn',
  alreadyUsed: '/error-page/already-used',
  /* Where the employee-benefits entry point rejects a link with no offer/plan/group codes
     (employee-benefits-guard.service.ts:21). */
  technicalDifficulties: '/error-page/technical-difficulties',
};

/**
 * Status codes the participant-token lookup returns for a family-plan invitation that cannot
 * be used, and where identityWeb sends each (enrollment-main.component.ts:213-230).
 */
export const PARTICIPANT_TOKEN_ERROR_PAGES: Record<number, string> = {
  400: ENROLLMENT_ERROR_PAGES.invalidParticipantToken,
  406: ENROLLMENT_ERROR_PAGES.serviceEndRequest,
  410: ENROLLMENT_ERROR_PAGES.invitationWithdrawn,
  416: ENROLLMENT_ERROR_PAGES.alreadyUsed,
  500: ENROLLMENT_ERROR_PAGES.somethingWentWrong,
};

/**
 * Server messages that mean "this offer cannot be enrolled under this brand".
 * identityWeb matches these on `error[0].message` (flex-enrollment.component.ts:166-172).
 */
export const FLEX_BRAND_ERROR_MESSAGES = ['BRAND_MISMATCH', 'MISSING_BRAND'];

/** identityWeb routes DIT identity-verification denials to their own page. */
export const FLEX_DIT_VERIFY_PROPERTY = 'DITVerify';
export const FLEX_DIT_VERIFY_DENY_CODE = 430;

/**
 * Identity-proofing failures on the personal-info step. identityWeb opens its PID-failure
 * dialog for both (enrollment-personal-details.component.ts:210-217).
 */
export const PID_FAILURE_ERROR_CODES = ['verificationFailed', 'securityFreeze'];

/**
 * Account-step API failures. identityWeb maps these off the response status and the offending
 * `property` (basic-info.component.ts:204-237); `afs2` gets its own duplicate-email wording.
 */
export const ACCOUNT_ERROR_MESSAGES = {
  emailExists:
    'This email address entered is being used by another account. Please enter a different email.',
  usernameExists:
    'Your email address is already in our system. To participate in this offer, please use a different email address.',
  invalidEmail:
    'There was an error when attempting to validate your email address. Please enter a valid email address.',
  emailAlreadyTaken: 'This email address is being used by another account.',
};

/** Properties the account endpoint reports a duplicate/invalid email against. */
export const ACCOUNT_EMAIL_ERROR_PROPERTIES = [
  'emailAddress',
  'emailAddress.username',
];

/**
 * Offer codes that carry Identity Defense's discounted first-year pricing, which replaces the
 * standard auto-renew consent wording (enrollment-plan-summary.component.ts:28).
 */
export const IDDEFENSE_DISCOUNT_OFFER_CODES = [
  '2abjyp47vz',
  'bknuyuzvqf',
  '65vcc8hpyu',
];

/**
 * Plan names the summary screens display in place of the offer's own plan name
 * (utils/plan-name-mapping.ts).
 */
export const PLAN_NAME_OVERRIDES: Record<string, string> = {
  finsecfamb: 'Financial Shield Pro',
  finsecindb: 'Financial Shield Pro',
  fs2crfam: 'Financial Shield Complete',
  fs2crind: 'Financial Shield Complete',
  fs3bcrfam: 'Financial Shield Total',
  fs3bcrind: 'Financial Shield Total',
};

/**
 * Financial Shield serves one retired offer/plan pair that must be swapped for its
 * replacement before the account is created (basic-info.component.ts:116-131).
 */
export const FINANCIAL_SHIELD_OFFER_REDIRECT = {
  host: 'financialshield.com',
  from: { offerCode: 'gkwi7g5wp9', planCode: 'finsecind' },
  to: { offerCode: '3f3nbeg07v', planCode: 'finsecind' },
};
